import { KeyboardEvent, RefObject } from "react";
import { SideNavContextProps } from "../SideNavContext";

/**
 * Handles keyboard navigation for SideNav components using arrow keys.
 *
 * This function manages focus movement between navigable elements in a SideNav,
 * providing circular navigation (wrapping from last to first and vice versa).
 * It updates tabindex attributes to maintain proper focus management and
 * prevents default browser behavior for arrow key navigation.
 *
 * @param e - The keyboard event that triggered the navigation
 * @param links - NodeList of all navigable elements in the SideNav
 * @param current - Ref object pointing to the currently focused element
 * @returns void
 */
export const sideNavKeyboardNavigation = (
  e: KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>,
  links: SideNavContextProps["links"],
  current: RefObject<HTMLAnchorElement | HTMLButtonElement>,
) => {
  const currentIndex = Array.prototype.indexOf.call(links, current.current);

  switch (e.key) {
    case "ArrowDown":
      if (!links) return;
      e.preventDefault();
      links[currentIndex].setAttribute("tabindex", "-1");
      if (currentIndex === links.length - 1) {
        links[0].setAttribute("tabindex", "0");
        (links[0] as HTMLAnchorElement | HTMLButtonElement).focus();
        break;
      }
      links[currentIndex + 1].setAttribute("tabindex", "0");
      (
        links[currentIndex + 1] as HTMLAnchorElement | HTMLButtonElement
      ).focus();
      break;
    case "ArrowUp":
      e.preventDefault();
      if (!links) return;
      links[currentIndex].setAttribute("tabindex", "-1");
      if (currentIndex === 0) {
        links[links.length - 1].setAttribute("tabindex", "0");
        (
          links[links.length - 1] as HTMLAnchorElement | HTMLButtonElement
        ).focus();
        break;
      }
      links[currentIndex - 1].setAttribute("tabindex", "0");
      (
        links[currentIndex - 1] as HTMLAnchorElement | HTMLButtonElement
      ).focus();
      break;
    default:
      break;
  }
};
